/**
 * Property Market Page
 * 부동산 목록 페이지 - 호주 특화 필터 포함
 */

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { useRegion } from "@/contexts/RegionContext";
import { LocationSelector } from "@/components/LocationSelector";
import { regions } from "@shared/schema";
import { Property, PropertyListQuery } from "@shared/schemas/properties";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import { Home, Eye, Heart, MapPin, Calendar, Building2, Bed, Car, Bath, Building, ShoppingCart, Key } from "lucide-react";
import { useState, useEffect } from "react";
import { Link } from "wouter";
import { cn } from "@/lib/utils";

// 지역 필터 (CarMarket과 동일)
const regionFilters = [
  { id: "ALL", label: "전체" },
  { id: "NSW", label: "시드니 (NSW)" },
  { id: "VIC", label: "멜번 (VIC)" },
  { id: "QLD", label: "브리즈번/케언즈 (QLD)" },
  { id: "SA", label: "아델레이드 (SA)" },
  { id: "WA", label: "퍼스 (WA)" },
  { id: "NT", label: "다윈 (NT)" },
  { id: "ACT", label: "캔버라 (ACT)" },
];

// Property Type 목록
const propertyTypes = [
  { value: "sale", label: "판매" },
  { value: "rent", label: "임대" },
  { value: "sold", label: "계약완료" },
];

// Listing Type 목록
const listingTypes = [
  { value: "house", label: "House" },
  { value: "apartment", label: "Apartment" },
  { value: "townhouse", label: "Townhouse" },
  { value: "unit", label: "Unit" },
  { value: "villa", label: "Villa" },
  { value: "room", label: "Room" },
  { value: "granny_flat", label: "Granny Flat" },
  { value: "homestay", label: "Homestay" },
  { value: "studio", label: "Studio" },
  { value: "commercial", label: "Commercial" },
  { value: "short_term", label: "Short-term" },
];

// Lease Term 목록
const leaseTerms = [
  { value: "6", label: "6개월" },
  { value: "12", label: "12개월" },
  { value: "24", label: "24개월" },
  { value: "36", label: "36개월" },
];

// API 응답 타입 정의
type PropertyListItem = Property & {
  businessName?: string | null;
  property_type?: string | null;
  parking_spaces?: number | null;
};
type PropertiesApiResponse = PropertyListItem[] | { ok: boolean; data?: PropertyListItem[] };

/**
 * API 응답을 정규화하여 항상 Property[] 배열을 반환
 */
function normalizeProperties(data: PropertiesApiResponse | undefined): PropertyListItem[] {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.data)) return data.data;
  return [];
}

export default function PropertyMarket() {
  const { user } = useAuth();
  const { selectedRegion, setSelectedRegion } = useRegion();
  const [selectedState, setSelectedState] = useState<string>("ALL");
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);

  // RegionContext의 selectedRegion과 동기화
  useEffect(() => {
    if (selectedRegion) {
      if (selectedRegion === "ALL") {
        setSelectedState("ALL");
      } else {
        setSelectedState(selectedRegion);
      }
    }
  }, [selectedRegion]);

  // selectedState 변경 시 RegionContext도 업데이트
  const handleStateChange = (state: string) => {
    setSelectedState(state);
    if (state !== "ALL") {
      setSelectedRegion(state as any);
    }
  };
  const [selectedPropertyType, setSelectedPropertyType] = useState<string>("all");
  const [selectedListingType, setSelectedListingType] = useState<string>("all");
  const [bedroomsMin, setBedroomsMin] = useState<string>("");
  const [bedroomsMax, setBedroomsMax] = useState<string>("");
  const [bathroomsMin, setBathroomsMin] = useState<string>("");
  const [bathroomsMax, setBathroomsMax] = useState<string>("");
  const [parkingMin, setParkingMin] = useState<string>("");
  const [parkingMax, setParkingMax] = useState<string>("");
  const [minPrice, setMinPrice] = useState<string>("");
  const [maxPrice, setMaxPrice] = useState<string>("");
  const [leaseTermMonths, setLeaseTermMonths] = useState<string>("all");
  const [billsIncluded, setBillsIncluded] = useState<boolean | undefined>(undefined);
  const [furnished, setFurnished] = useState<boolean | undefined>(undefined);
  const [petAllowed, setPetAllowed] = useState<boolean | undefined>(undefined);

  const { data: propertiesData, isLoading } = useQuery<PropertiesApiResponse>({
    queryKey: [
      "/api/properties",
      selectedState,
      selectedPropertyType,
      selectedListingType,
      bedroomsMin,
      bedroomsMax,
      bathroomsMin,
      bathroomsMax,
      parkingMin,
      parkingMax,
      minPrice,
      maxPrice,
      leaseTermMonths,
      billsIncluded,
      furnished,
      petAllowed,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedState !== "ALL") params.append("state", selectedState);
      if (selectedPropertyType && selectedPropertyType !== "all") params.append("property_type", selectedPropertyType);
      if (selectedListingType && selectedListingType !== "all") params.append("listing_type", selectedListingType);
      if (bedroomsMin) params.append("bedrooms_min", bedroomsMin);
      if (bedroomsMax) params.append("bedrooms_max", bedroomsMax);
      if (bathroomsMin) params.append("bathrooms_min", bathroomsMin);
      if (bathroomsMax) params.append("bathrooms_max", bathroomsMax);
      if (parkingMin) params.append("parking_min", parkingMin);
      if (parkingMax) params.append("parking_max", parkingMax);
      if (minPrice) params.append("price_min", String(parseInt(minPrice) * 100)); // Convert to cents
      if (maxPrice) params.append("price_max", String(parseInt(maxPrice) * 100));
      if (leaseTermMonths && leaseTermMonths !== "all") params.append("lease_term_months", leaseTermMonths);
      if (billsIncluded !== undefined) params.append("bills_included", String(billsIncluded));
      if (furnished !== undefined) params.append("furnished", String(furnished));
      if (petAllowed !== undefined) params.append("pet_allowed", String(petAllowed));
      params.append("limit", "50");

      const res = await fetch(`/api/properties?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to fetch properties");
      const json = await res.json();
      return json;
    },
  });

  // API 응답을 정규화하여 항상 Property[] 배열 보장
  const properties = normalizeProperties(propertiesData);

  const formatPrice = (priceCents: number, pricePeriod?: string | null) => {
    if (priceCents === 0) return "무료";
    if (priceCents === -1) return "가격협의";
    const price = Math.round(priceCents / 100);
    if (pricePeriod === "weekly") {
      return `$${price.toLocaleString()}/week`;
    } else if (pricePeriod === "monthly") {
      return `$${price.toLocaleString()}/month`;
    }
    return `$${price.toLocaleString()}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">부동산</h1>
          <p className="text-muted-foreground">
            호주 교민을 위한 안전한 부동산 거래 플랫폼
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Filter Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardHeader>
                <h2 className="text-lg font-semibold">필터</h2>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* 지역 필터 - 주요 지역 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    주요 지역
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {regionFilters.map((region) => {
                      const isSelected = selectedState === region.id;
                      return (
                        <button
                          key={region.id}
                          onClick={() => handleStateChange(region.id)}
                          className={cn(
                            "text-xs px-2 py-1 rounded transition-colors",
                            isSelected
                              ? "bg-[#60a6fa] text-white"
                              : "bg-muted text-foreground hover:bg-muted/80"
                          )}
                        >
                          {region.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 지역변경 버튼 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    지역 변경
                  </label>
                  <LocationSelector
                    variant="button"
                    open={locationDialogOpen}
                    onOpenChange={setLocationDialogOpen}
                  />
                </div>

                {/* Property Type 필터 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">매물 유형</label>
                  <Select value={selectedPropertyType} onValueChange={setSelectedPropertyType}>
                    <SelectTrigger>
                      <SelectValue placeholder="매물 유형 선택" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">전체</SelectItem>
                      {propertyTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Listing Type 필터 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">건물 유형</label>
                  <Select value={selectedListingType} onValueChange={setSelectedListingType}>
                    <SelectTrigger>
                      <SelectValue placeholder="건물 유형 선택" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">전체</SelectItem>
                      {listingTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Bedrooms 필터 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">침실</label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="최소"
                      value={bedroomsMin}
                      onChange={(e) => setBedroomsMin(e.target.value)}
                      min="0"
                    />
                    <Input
                      type="number"
                      placeholder="최대"
                      value={bedroomsMax}
                      onChange={(e) => setBedroomsMax(e.target.value)}
                      min="0"
                    />
                  </div>
                </div>

                {/* Bathrooms 필터 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">욕실</label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="최소"
                      value={bathroomsMin}
                      onChange={(e) => setBathroomsMin(e.target.value)}
                      min="0"
                    />
                    <Input
                      type="number"
                      placeholder="최대"
                      value={bathroomsMax}
                      onChange={(e) => setBathroomsMax(e.target.value)}
                      min="0"
                    />
                  </div>
                </div>

                {/* Parking 필터 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">주차</label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="최소"
                      value={parkingMin}
                      onChange={(e) => setParkingMin(e.target.value)}
                      min="0"
                    />
                    <Input
                      type="number"
                      placeholder="최대"
                      value={parkingMax}
                      onChange={(e) => setParkingMax(e.target.value)}
                      min="0"
                    />
                  </div>
                </div>

                {/* 가격 범위 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">가격 범위 (AUD)</label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="최소"
                      value={minPrice}
                      onChange={(e) => setMinPrice(e.target.value)}
                    />
                    <Input
                      type="number"
                      placeholder="최대"
                      value={maxPrice}
                      onChange={(e) => setMaxPrice(e.target.value)}
                    />
                  </div>
                </div>

                {/* Lease Term 필터 (렌트용) */}
                {selectedPropertyType === "rent" && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">계약 기간</label>
                    <Select value={leaseTermMonths} onValueChange={setLeaseTermMonths}>
                      <SelectTrigger>
                        <SelectValue placeholder="계약 기간 선택" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">전체</SelectItem>
                        {leaseTerms.map((term) => (
                          <SelectItem key={term.value} value={term.value}>
                            {term.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Bills Included 필터 */}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="bills_included"
                    checked={billsIncluded === true}
                    onCheckedChange={(checked) => setBillsIncluded(checked ? true : undefined)}
                  />
                  <label htmlFor="bills_included" className="text-sm font-medium">
                    Bills Included
                  </label>
                </div>

                {/* Furnished 필터 */}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="furnished"
                    checked={furnished === true}
                    onCheckedChange={(checked) => setFurnished(checked ? true : undefined)}
                  />
                  <label htmlFor="furnished" className="text-sm font-medium">
                    가구 포함
                  </label>
                </div>

                {/* Pet Allowed 필터 */}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="pet_allowed"
                    checked={petAllowed === true}
                    onCheckedChange={(checked) => setPetAllowed(checked ? true : undefined)}
                  />
                  <label htmlFor="pet_allowed" className="text-sm font-medium">
                    반려동물 허용
                  </label>
                </div>

                {/* 필터 초기화 */}
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setSelectedState("ALL");
                    setSelectedPropertyType("all");
                    setSelectedListingType("all");
                    setBedroomsMin("");
                    setBedroomsMax("");
                    setBathroomsMin("");
                    setBathroomsMax("");
                    setParkingMin("");
                    setParkingMax("");
                    setMinPrice("");
                    setMaxPrice("");
                    setLeaseTermMonths("all");
                    setBillsIncluded(undefined);
                    setFurnished(undefined);
                    setPetAllowed(undefined);
                  }}
                >
                  필터 초기화
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Property List */}
          <div className="lg:col-span-3">
            {isLoading ? (
              <div className="py-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[...Array(6)].map((_, i) => (
                    <Card key={i}>
                      <Skeleton className="h-48 w-full" />
                      <CardHeader>
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-4 w-1/2" />
                      </CardHeader>
                      <CardContent>
                        <Skeleton className="h-4 w-full" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <p className="text-center text-sm text-muted-foreground mt-4">
                  매물 정보를 불러오는 중입니다…
                </p>
              </div>
            ) : properties.length === 0 ? (
              <Card className="p-12 text-center">
                <Home className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-4">
                  조건에 맞는 매물이 없습니다.
                </p>
                {user && (
                  <Button asChild>
                    <Link href="/properties/new">부동산 등록하기</Link>
                  </Button>
                )}
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {properties.map((property) => {
                  // 이미지 배열 생성 (primary_image + images)
                  const allImages: string[] = [];
                  if (property.primaryImage) {
                    allImages.push(property.primaryImage);
                  }
                  if (property.images && Array.isArray(property.images)) {
                    property.images.forEach((img) => {
                      if (img && !allImages.includes(img)) {
                        allImages.push(img);
                      }
                    });
                  }
                  const mainImage = allImages[0] || null;
                  const imageCount = allImages.length;

                  return (
                    <Link key={property.id} href={`/properties/${property.id}`}>
                      <Card className="group overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg border-0 shadow-md">
                        {/* 에이전트 배너 - Realestate.com.au 스타일 */}
                        {property.isAgentListing && (
                          <div className="bg-black text-white p-3 flex items-center justify-between">
                            <span className="font-semibold text-sm">
                              {property.businessName || "에이전트"}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-sm">
                                {property.sellerDisplayName || property.sellerNickname || "에이전트"}
                              </span>
                              <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                                <Building2 className="w-4 h-4" />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* 메인 이미지 - Realestate.com.au 스타일 */}
                        <div className="relative w-full aspect-video overflow-hidden bg-muted">
                          {mainImage ? (
                            <>
                              <img
                                src={mainImage}
                                alt={property.listingType}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                loading="lazy"
                                onError={(e) => {
                                  e.currentTarget.src =
                                    'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600"%3E%3Crect fill="%23e5e7eb" width="800" height="600"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="32" dy="310" dx="400" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                                }}
                              />
                              {/* 매물 유형 배지 - 오른쪽 상단 맨 위 */}
                              {(() => {
                                // status가 'sold' 또는 'rented'이면 계약완료
                                const isSold = property.status === "sold" || property.status === "rented";
                                const propertyType = property.property_type || property.propertyType || "";
                                const displayType = isSold ? "sold" : propertyType;

                                // property_type이 없으면 표시하지 않음
                                if (!displayType && !isSold) return null;

                                return (
                                  <Badge
                                    className={`absolute top-2 right-2 text-white backdrop-blur-sm text-[10px] px-1.5 py-0.5 font-semibold z-10 ${
                                      displayType === "rent"
                                        ? "bg-blue-600"
                                        : displayType === "sale"
                                        ? "bg-green-600"
                                        : "bg-gray-600"
                                    }`}
                                  >
                                    {displayType === "rent"
                                      ? "임대"
                                      : displayType === "sale"
                                      ? "판매"
                                      : "계약완료"}
                                  </Badge>
                                );
                              })()}
                              {/* 이미지 카운터 - 매물 유형 아래 */}
                              {imageCount > 1 && (
                                <Badge className="absolute top-10 right-2 bg-black/70 text-white backdrop-blur-sm text-[10px] px-1.5 py-0.5">
                                  +{imageCount - 1}
                                </Badge>
                              )}
                            </>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Home className="w-16 h-16 text-muted-foreground" />
                            </div>
                          )}
                          {/* 프리미엄 배지 */}
                          {property.isPremium && (
                            <Badge className="absolute top-2 left-12 bg-yellow-500 text-white">
                              프리미엄
                            </Badge>
                          )}
                        </div>

                        {/* 정보 하단 - Realestate.com.au 스타일 */}
                        <CardContent className="p-4">
                          <div className="space-y-2">
                            {/* 주소 */}
                            <h3 className="font-bold text-base group-hover:text-primary transition-colors line-clamp-1">
                              {property.address || `${property.suburb || property.city}, ${property.state}`}
                            </h3>

                            {/* 가격 */}
                            <div className="flex items-baseline gap-2">
                              <p className="font-bold text-xl text-primary">
                                {formatPrice(property.price, property.pricePeriod)}
                              </p>
                              {property.bondAmount && (property.property_type === "rent" || property.propertyType === "rent") && (
                                <p className="text-sm text-muted-foreground">
                                  Bond ${Math.round(property.bondAmount / 100).toLocaleString()}
                                </p>
                              )}
                            </div>

                            {/* 침실/욕실/주차 - 호주 표준 형식 (3/2/2) with icons */}
                            <div className="flex items-center gap-2 text-sm font-medium">
                              <div className="flex items-center gap-1">
                                <Bed className="h-4 w-4 text-muted-foreground" />
                                {property.bedrooms !== null && property.bedrooms !== undefined ? (
                                  <span>{property.bedrooms}</span>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </div>
                              <span className="text-muted-foreground">/</span>
                              <div className="flex items-center gap-1">
                                <Bath className="h-4 w-4 text-muted-foreground" />
                                {property.bathrooms !== null && property.bathrooms !== undefined ? (
                                  <span>{property.bathrooms}</span>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </div>
                              <span className="text-muted-foreground">/</span>
                              <div className="flex items-center gap-1">
                                <Car className="h-4 w-4 text-muted-foreground" />
                                {property.parking_spaces !== null && property.parking_spaces !== undefined ? (
                                  <span>{property.parking_spaces}</span>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </div>
                              <span className="text-muted-foreground ml-2 capitalize text-xs">
                                {property.listingType}
                              </span>
                            </div>

                            {/* Inspection 일정 (렌트) */}
                            {(property.property_type === "rent" || property.propertyType === "rent") && property.inspectionDate && (() => {
                              try {
                                const inspectionDate = new Date(property.inspectionDate);
                                if (isNaN(inspectionDate.getTime())) {
                                  return (
                                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                      <Calendar className="h-3 w-3" />
                                      <span>
                                        Inspection: {property.inspectionDate}
                                        {property.inspectionTime && ` ${property.inspectionTime}`}
                                      </span>
                                    </div>
                                  );
                                }
                                return (
                                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                    <Calendar className="h-3 w-3" />
                                    <span>
                                      Inspection: {inspectionDate.toLocaleDateString("ko-KR", {
                                        weekday: "short",
                                        month: "short",
                                        day: "numeric",
                                      })}
                                      {property.inspectionTime && ` ${property.inspectionTime}`}
                                    </span>
                                  </div>
                                );
                              } catch (error) {
                                return (
                                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                    <Calendar className="h-3 w-3" />
                                    <span>
                                      Inspection: {property.inspectionDate}
                                      {property.inspectionTime && ` ${property.inspectionTime}`}
                                    </span>
                                  </div>
                                );
                              }
                            })()}

                            {/* Auction 일정 (판매) */}
                            {(property.property_type === "sale" || property.propertyType === "sale") && property.priceType === "auction" && property.inspectionDate && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Calendar className="h-3 w-3" />
                                <span>
                                  Auction: {new Date(property.inspectionDate).toLocaleDateString("ko-KR", {
                                    weekday: "short",
                                    month: "short",
                                    day: "numeric",
                                  })}
                                  {property.inspectionTime && ` ${property.inspectionTime}`}
                                </span>
                              </div>
                            )}

                            {/* 하단 메타 정보 */}
                            <div className="flex items-center justify-between pt-2 border-t border-border/30">
                              <div className="flex items-center gap-2 flex-wrap">
                                {property.furnished && (
                                  <Badge variant="outline" className="text-xs">가구</Badge>
                                )}
                                {property.petAllowed && (
                                  <Badge variant="outline" className="text-xs">반려동물</Badge>
                                )}
                                {property.billsIncluded && (
                                  <Badge variant="outline" className="text-xs">Bills</Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Eye className="h-3 w-3" />
                                <span>{property.views || 0}</span>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            )}

            {/* 등록 버튼 */}
            {user && (
              <div className="mt-8 text-center">
                <Button asChild size="lg">
                  <Link href="/properties/new">부동산 등록하기</Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
